/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
import defineProperty from '../defineProperty'
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import sha256 from 'crypto-js/sha256'
import hmacSHA256 from 'crypto-js/hmac-sha256'
import CryptoJS from 'crypto-js'
import url from 'url'
import utils from './utils'
import type { ApiGatewayRequest } from '../../awsApiGatewayClient'
import encHex from 'crypto-js/enc-hex'

function ownKeys(object: object, enumerableOnly: boolean) {
  const keys = Object.keys(object)
  if (Object.getOwnPropertySymbols) {
    let symbols = Object.getOwnPropertySymbols(object)
    if (enumerableOnly) {
      // @ts-expect-error JavaScript由来のコード故
      symbols = symbols.filter(sym => Object.getOwnPropertyDescriptor(object, sym).enumerable)
    }
    // @ts-expect-error JavaScript由来のコード故
    keys.push(...symbols)
  }
  return keys
}

// @ts-expect-error JavaScript由来のコード故
function _objectSpread(target, ...sources) {
  sources.forEach((source, i) => {
    source = source != null ? source : {}
    if (i % 2) {
      ownKeys(source, true).forEach(key => {
        defineProperty(target, key, source[key])
      })
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source))
    } else {
      // @ts-expect-error JavaScript由来のコード故
      ownKeys(source).forEach(key => {
        // @ts-expect-error JavaScript由来のコード故
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key))
      })
    }
  })
  return target
}

export interface SigV4ClientConfig {
  accessKey: string | undefined
  secretKey: string | undefined
  sessionToken: string | undefined
  serviceName: string | undefined
  region: string | undefined
  endpoint: string | undefined
  retries: number | undefined
  retryCondition: string | undefined
  retryDelay: string | number | (() => number) | undefined
  host: string | undefined
  defaultContentType: string | undefined
  defaultAcceptType: string | undefined
  systemClockOffset: number | undefined
}

export interface SigV4ClientFactory {
  newClient: ((config: SigV4ClientConfig) => SigV4Client) | undefined
}

export const sigV4ClientFactory: SigV4ClientFactory = {
  newClient: undefined
}

export interface Request {
  verb: string
  path: string
  queryParams: KeyValues
  timeout: number
  headers: KeyValues
  body: string | undefined | null
}

export interface SigV4Client {
  hash: (value: string) => CryptoJS.lib.WordArray
  hmac: (
    secret: CryptoJS.lib.WordArray | string,
    value: CryptoJS.lib.WordArray | string
  ) => CryptoJS.lib.WordArray
  buildCanonicalRequest: (
    method: string,
    path: string,
    queryParams: KeyValues,
    headers: KeyValues,
    payload: string
  ) => string
  hashCanonicalRequest: (request: string) => CryptoJS.lib.WordArray
  buildCanonicalUri: (uri: string) => string
  buildCanonicalQueryString: (queryParams: KeyValues) => string
  fixedEncodeURIComponent: (str: string) => string
  buildCanonicalHeaders: (headers: KeyValues) => string
  buildCanonicalSignedHeaders: (headers: KeyValues) => string
  buildStringToSign: (
    datetime: string,
    credentialScope: string,
    hashedCanonicalRequest: string
  ) => string
  buildCredentialScope: (datetime: string, region: string, service: string) => string
  calculateSigningKey: (
    secretKey: string,
    datetime: string,
    region: string,
    service: string
  ) => CryptoJS.lib.WordArray
  calculateSignature: (key: string, stringToSign: string) => CryptoJS.lib.WordArray
  buildAuthorizationHeader: (
    accessKey: string,
    credentialScope: string,
    headers: KeyValues,
    signature: string
  ) => string
  makeRequest: (request: ApiGatewayRequest) => Promise<AxiosResponse<any, any>>
}

interface KeyValues {
  [key: string]: string
}

sigV4ClientFactory.newClient = function (config: SigV4ClientConfig): SigV4Client {
  const AWS_SHA_256 = 'AWS4-HMAC-SHA256'
  const AWS4_REQUEST = 'aws4_request'
  const AWS4 = 'AWS4'
  const X_AMZ_DATE = 'x-amz-date'
  const X_AMZ_SECURITY_TOKEN = 'x-amz-security-token'
  const HOST = 'host'
  const AUTHORIZATION = 'Authorization'

  function hash(value: string): CryptoJS.lib.WordArray {
    return sha256(value)
  }

  function hexEncode(value: CryptoJS.lib.WordArray) {
    return value.toString(encHex)
  }

  function hmac(
    secret: CryptoJS.lib.WordArray | string,
    value: CryptoJS.lib.WordArray | string
  ): CryptoJS.lib.WordArray {
    return hmacSHA256(value, secret)
  }

  function buildCanonicalRequest(
    method: string,
    path: string,
    queryParams: KeyValues,
    headers: KeyValues,
    payload: string
  ): string {
    // return method + '\n' + buildCanonicalUri(path) + '\n' + buildCanonicalQueryString(queryParams) + '\n' + buildCanonicalHeaders(headers) + '\n' + buildCanonicalSignedHeaders(headers) + '\n' + hexEncode(hash(payload));
    return (
      method +
      '\n' +
      buildCanonicalUri(path) +
      '\n' +
      buildCanonicalQueryString(queryParams) +
      '\n' +
      buildCanonicalHeaders(headers) +
      '\n' +
      buildCanonicalSignedHeaders(headers) +
      '\n' +
      hash(payload)
    )
  }

  function hashCanonicalRequest(request: string): CryptoJS.lib.WordArray {
    // return hexEncode(hash(request));
    return hash(request)
  }

  function buildCanonicalUri(uri: string): string {
    return encodeURI(uri)
  }

  function buildCanonicalQueryString(queryParams: KeyValues): string {
    if (Object.keys(queryParams).length < 1) {
      return ''
    }

    const sortedQueryParams = []

    for (const property in queryParams) {
      if (Object.prototype.hasOwnProperty.call(queryParams, property)) {
        sortedQueryParams.push(property)
      }
    }

    sortedQueryParams.sort()
    let canonicalQueryString = ''

    for (let i = 0; i < sortedQueryParams.length; i++) {
      canonicalQueryString +=
        sortedQueryParams[i] +
        '=' +
        fixedEncodeURIComponent(queryParams[sortedQueryParams[i]]) +
        '&'
    }

    return canonicalQueryString.substring(0, canonicalQueryString.length - 1)
  }

  function fixedEncodeURIComponent(str: string): string {
    return encodeURIComponent(str).replace(/[!'()*]/g, function (c) {
      return '%' + c.charCodeAt(0).toString(16)
    })
  }

  function buildCanonicalHeaders(headers: KeyValues): string {
    let canonicalHeaders = ''
    const sortedKeys = []

    for (const property in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, property)) {
        sortedKeys.push(property)
      }
    }

    sortedKeys.sort(function (a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase())
    })

    for (let i = 0; i < sortedKeys.length; i++) {
      canonicalHeaders += sortedKeys[i].toLowerCase() + ':' + headers[sortedKeys[i]] + '\n'
    }

    return canonicalHeaders
  }

  function buildCanonicalSignedHeaders(headers: KeyValues): string {
    const sortedKeys = []

    for (const property in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, property)) {
        sortedKeys.push(property.toLowerCase())
      }
    }

    sortedKeys.sort()
    return sortedKeys.join(';')
  }

  function buildStringToSign(
    datetime: string,
    credentialScope: string,
    hashedCanonicalRequest: string
  ): string {
    return AWS_SHA_256 + '\n' + datetime + '\n' + credentialScope + '\n' + hashedCanonicalRequest
  }

  function buildCredentialScope(datetime: string, region: string, service: string): string {
    return datetime.substring(0, 8) + '/' + region + '/' + service + '/' + AWS4_REQUEST
  }

  function calculateSigningKey(
    secretKey: string,
    datetime: string,
    region: string,
    service: string
  ): CryptoJS.lib.WordArray {
    return hmac(
      hmac(hmac(hmac(AWS4 + secretKey, datetime.substring(0, 8)), region), service),
      AWS4_REQUEST
    )
  }

  function calculateSignature(key: CryptoJS.lib.WordArray, stringToSign: string): string {
    return hexEncode(hmac(key, stringToSign))
  }

  function buildAuthorizationHeader(
    accessKey: string,
    credentialScope: string,
    headers: KeyValues,
    signature: string
  ): string {
    return (
      AWS_SHA_256 +
      ' Credential=' +
      accessKey +
      '/' +
      credentialScope +
      ', SignedHeaders=' +
      buildCanonicalSignedHeaders(headers) +
      ', Signature=' +
      signature
    )
  }

  interface AwsSigV4Client {
    accessKey: string | undefined
    secretKey: string | undefined
    sessionToken: string | undefined
    serviceName: string | undefined
    region: string | undefined
    endpoint: string | undefined
    retries: number | undefined
    retryCondition: string | undefined
    retryDelay: string | number | (() => number) | undefined
    host: string | undefined
  }

  // @ts-expect-error we define properties soon later
  const awsSigV4Client: SigV4Client & AwsSigV4Client = {}

  if (config.accessKey === undefined || config.secretKey === undefined) {
    return awsSigV4Client
  }

  awsSigV4Client.accessKey = utils.assertDefined(config.accessKey, 'accessKey')
  awsSigV4Client.secretKey = utils.assertDefined(config.secretKey, 'secretKey')
  awsSigV4Client.sessionToken = config.sessionToken
  awsSigV4Client.serviceName = utils.assertDefined(config.serviceName, 'serviceName')
  awsSigV4Client.region = utils.assertDefined(config.region, 'region')
  awsSigV4Client.endpoint = utils.assertDefined(config.endpoint, 'endpoint')
  awsSigV4Client.retries = config.retries
  awsSigV4Client.retryCondition = config.retryCondition
  awsSigV4Client.retryDelay = config.retryDelay
  awsSigV4Client.host = config.host

  awsSigV4Client.makeRequest = function (
    request: ApiGatewayRequest
  ): Promise<AxiosResponse<any, any>> {
    const verb = utils.assertDefined(request.verb, 'verb')
    const path = utils.assertDefined(request.path, 'path')
    let queryParams = utils.copy(request.queryParams)
    let timeout = utils.copy(request.timeout)

    if (queryParams === undefined) {
      queryParams = {}
    }

    if (timeout === undefined) {
      timeout = 0
    }

    let headers = utils.copy(request.headers)

    if (headers === undefined) {
      headers = {}
    } // If the user has not specified an override for Content type the use default

    if (headers['Content-Type'] === undefined) {
      headers['Content-Type'] = config.defaultContentType
    } // If the user has not specified an override for Accept type the use default

    if (headers['Accept'] === undefined) {
      headers['Accept'] = config.defaultAcceptType
    }

    let body = utils.copy(request.body) // stringify request body if content type is JSON

    if (body && headers['Content-Type'] && headers['Content-Type'] === 'application/json') {
      body = JSON.stringify(body)
    } // If there is no body remove the content-type header so it is not included in SigV4 calculation

    if (body === '' || body === undefined || body === null) {
      delete headers['Content-Type']
    }

    const datetime = new Date(new Date().getTime() + config.systemClockOffset!)
      .toISOString()
      .replace(/\.\d{3}Z$/, 'Z')
      .replace(/[:-]|\.\d{3}/g, '')
    headers[X_AMZ_DATE] = datetime

    if (awsSigV4Client.host) {
      headers[HOST] = awsSigV4Client.host
    } else {
      const parser = url.parse(awsSigV4Client.endpoint!)

      headers[HOST] = parser.hostname
    }

    const canonicalRequest = buildCanonicalRequest(verb, path, queryParams, headers, body)
    const hashedCanonicalRequest = hashCanonicalRequest(canonicalRequest)
    const credentialScope = buildCredentialScope(
      datetime,
      awsSigV4Client.region!,
      awsSigV4Client.serviceName!
    )
    const stringToSign = buildStringToSign(
      datetime,
      credentialScope,
      hashedCanonicalRequest.toString()
    )
    const signingKey = calculateSigningKey(
      awsSigV4Client.secretKey!,
      datetime,
      awsSigV4Client.region!,
      awsSigV4Client.serviceName!
    )
    const signature = calculateSignature(signingKey, stringToSign)
    headers[AUTHORIZATION] = buildAuthorizationHeader(
      awsSigV4Client.accessKey!,
      credentialScope,
      headers,
      signature
    )

    if (awsSigV4Client.sessionToken !== undefined && awsSigV4Client.sessionToken !== '') {
      headers[X_AMZ_SECURITY_TOKEN] = awsSigV4Client.sessionToken
    }

    delete headers[HOST]
    let theUrl = config.endpoint + path
    const queryString = buildCanonicalQueryString(queryParams)

    if (queryString !== '') {
      theUrl += '?' + queryString
    } // Need to re-attach Content-Type if it is not specified at this point

    if (headers['Content-Type'] === undefined) {
      headers['Content-Type'] = config.defaultContentType
    }

    const signedRequest: AxiosRequestConfig<any> = {
      headers: headers,
      timeout: timeout,
      data: body,
      method: verb,
      url: theUrl,
      baseURL: undefined
    }

    if (config.retries !== undefined) {
      signedRequest.baseURL = theUrl

      const client = axios.create(signedRequest) // Allow user configurable delay, or built-in exponential delay

      let retryDelay = function retryDelay() {
        return 0
      }

      if (config.retryDelay === 'exponential') {
        retryDelay = axiosRetry.exponentialDelay
      } else if (typeof config.retryDelay === 'string') {
        retryDelay = function retryDelay() {
          return parseInt(config.retryDelay as string)
        }
      } else if (typeof config.retryDelay === 'number') {
        retryDelay = function retryDelay() {
          return config.retryDelay as number
        }
      } else if (typeof config.retryDelay === 'function') {
        retryDelay = config.retryDelay
      }

      axiosRetry(
        client,
        _objectSpread(
          _objectSpread({}, config),
          {},
          {
            retryCondition: config.retryCondition,
            retryDelay: retryDelay
          }
        )
      )
      return client.request(signedRequest)
    }

    return axios(signedRequest)
  }

  return awsSigV4Client
}

export default sigV4ClientFactory
